import {
  computed, watch, ref,
} from '@vue/composition-api'

import debounce from 'lodash/debounce'
import { groupBy } from 'lodash'
import useDataTable from '@/compositions/useDataTable'
import useReload from '@/compositions/useReload'
import DatePicker from '@/components/DatePicker/DatePicker'
import MoreText from '@/components/MoreText/MoreText'
import useRequest from '@/compositions/useRequest'
import OwnerAction from '../OwnerAction/OwnerAction'
import ClientAction from '../ClientAction/ClientAction'
import { min, short } from '@/utils/formatDate'
import { statesToColor } from '@/constants/orders'
import { sortDatesArray } from '@/utils/sort'
import { measureUnits } from '@/constants/measureUnits'

const createHeader = ({
  text, value, sortable = false, width,
}) => ({
  text, sortable, value, width,
})

export default ({
  props: {
    type: String,
    state: String,
  },
  components: {
    DatePicker,
    MoreText,
    OwnerAction,
    ClientAction,
  },
  setup(props, context) {
    const {
      $store, $mq,
    } = context.root

    const search = ref('')
    const dates = ref([])
    const other = ref({})
    const headers = ref([])

    const myCompanyId = computed(() => $store.state.auth.company?.id)
    const requestType = computed(() => {
      const { type } = props
      const typeString = type === 'owner' ? 'fetchAllOwner' : 'fetchAll'
      return `materialDeliveries/${typeString}`
    })

    const getPrice = price => (price % 1 ? parseFloat(price).toFixed(1) : Math.round(price))

    const {
      options, getData, data, loading, totalCount, update,
    } = useDataTable({
      context,
      noInitial: true,
      lookupKey: 'deliveries',
      request: payload => $store.dispatch(requestType.value, { state: props.state, ...payload }),
    })

    const setOther = () => {
      let result = {}

      const searchText = `%${search.value}%`
      if (search.value !== '') {
        result = {
          m: 'or',
          id_matches: searchText,
          price_matches: searchText,
          points_address_matches: searchText,
          cargos_material_name_matches: searchText,
          cargos_material_type_name_matches: searchText,
          state: '',
        }
      } else {
        result = {
          state_eq: '',
        }
      }
      result = { ...result, state: props.state }

      const datesValue = dates.value
      if (datesValue.length > 0) {
        const datesResult = {
          end_time_between: sortDatesArray([datesValue[0 % datesValue.length], datesValue[1 % datesValue.length]]),
          created_at_between: sortDatesArray([datesValue[0 % datesValue.length], datesValue[1 % datesValue.length]]),
        }
        result = { ...result, ...datesResult }
      }

      other.value = result
    }

    const debouncedSetOther = debounce(setOther, 500, {
      leading: false,
      trailing: true,
    })

    const deleteRequest = useRequest({
      context,
      successMessage: 'Заказ материалов успешно удален',
      request: updateData => $store.dispatch('materialDeliveries/update', updateData),
    })

    const deleteMaterialDelivery = async (id) => {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Точно хотите удалить?')) return
      await deleteRequest.makeRequest({ id, data: { state: 'cancelled' } })
      update({ state_eq: '' })
    }

    const getOrderState = (order) => {
      const status = order.state
      if (status === 'active') return 'Активный заказ'
      if (status === 'cancelled' || status === 'done') return 'Заказ завершен'
      if (props.type === 'owner') return 'Заявка получена'
      return 'Тендер создан'
    }

    const setupHeaders = () => {
      const { type, state } = props
      headers.value = [
        createHeader({
          text: state === 'tender' ? '№ Тендера' : '№ Заказа',
          value: 'id',
          width: '5%',
        }),
        createHeader({ text: 'Описание стройматериалов', value: 'cargos', width: '15%' }),
        createHeader({ text: 'Контактная информация', value: 'client', width: '10%' }),
        createHeader({ text: 'Доставка', value: 'delivery', width: '18%' }),
        createHeader({ text: 'Комментарий', value: 'comment', width: '18%' }),
        createHeader({ text: 'Адрес самовывоза', value: 'deliveryAddress', width: '18%' }),
      ]

      if (state !== 'tender') {
        headers.value.splice(5, 0, createHeader({ text: 'Стоимость', value: 'price', width: '5%' }))
      } else {
        const actionText = $mq !== 'sm' ? 'Действие' : ''
        headers.value.push(createHeader({
          text: actionText, value: 'action', sortable: false, width: '10%',
        }))
      }

      if (type === 'client') {
        headers.value.splice(5, 0, createHeader({ text: 'Подрядчик', value: 'contractor', width: '15%' }))
        headers.value.push(createHeader({ text: '', value: 'delete', width: '3%' }))
      }
    }

    const updateTable = () => {
      update({ state_eq: '' })
      search.value = ''
      // dates.value = []
      setupHeaders()
    }

    const groupMaterials = (materials) => {
      const materialsWithPropertyName = materials.map(m => ({
        ...m,
        materialPropertyName: m.materialProperty.name,
      }))
      return groupBy(materialsWithPropertyName, 'materialPropertyName')
    }

    const getStatusColor = state => statesToColor[state] || 'warning'

    const getMyOffer = (item) => {
      const myOffer = item.tenderOffers.find(offer => offer.companyId === myCompanyId.value)
      return myOffer
    }

    const getMeasureUnitTemplate = unit => measureUnits[unit]?.html || unit
    const getPeriod = (item) => {
      const start = item.startTime ? short(new Date(item.startTime)) : null
      const end = short(new Date(item.endTime))
      let str = end
      if (start && start !== end) str = start + str
      return str
    }

    const items = computed(
      () => data.value
        .map(item => ({
          ...item,
          date: item.createdAt ? min(new Date(item.createdAt)) : '',
          isCash: item.payment === 'cash',
          createdAt: short(new Date(item.createdAt)),
          deliveryDate: getPeriod(item),
          cargos: item.cargos.map(cargo => ({
            ...cargo,
            groupedMaterials: groupMaterials(cargo.materialValues),
          })),
          price: getPrice(item.price),
        })),
    )

    watch(() => props.type, () => update({ state_eq: '' }))
    watch(() => props.state, () => update({ state_eq: '' }))
    watch(() => other.value, () => update(other.value))
    watch(() => search.value, debouncedSetOther)
    watch(() => dates.value, debouncedSetOther)
    updateTable()
    useReload({ cb: getData })

    return {
      loading,
      items,
      headers,
      options,
      search,
      dates,
      totalCount,
      getMeasureUnitTemplate,
      getStatusColor,
      getOrderState,
      getMyOffer,
      deleteMaterialDelivery,
      updateTable,
    }
  },
})
